export interface HumanConnectionProofApi {
  challenge(connectionId: string, signal: AbortSignal): Promise<string>;
  confirm(
    challengeId: string,
    nonce: string,
    signal: AbortSignal
  ): Promise<string>;
}

/** Accept a hub nonce only for the challenge issued by this device's HTTP request. */
export class HumanConnectionBinding {
  private pending?: { frames: Map<string, string>; wake: () => void };

  constructor(private readonly api: HumanConnectionProofApi) {}

  receive(value: unknown): void {
    if (!this.pending || !value || typeof value !== 'object') return;
    const frame = value as { challengeId?: unknown; nonce?: unknown };
    if (
      typeof frame.challengeId !== 'string' ||
      !/^[a-f0-9]{32}$/.test(frame.challengeId) ||
      typeof frame.nonce !== 'string' ||
      !/^[A-F0-9]{64}$/.test(frame.nonce)
    )
      return;
    // Bounded early arrivals: hub output can beat the challenge HTTP response.
    if (this.pending.frames.size >= 8) return;
    this.pending.frames.set(frame.challengeId, frame.nonce);
    this.pending.wake();
  }

  async bind(connectionId: string, signal: AbortSignal): Promise<string> {
    if (this.pending || signal.aborted || !connectionId)
      throw new Error('Connection binding unavailable');
    const operation = new AbortController();
    const abort = () => operation.abort();
    signal.addEventListener('abort', abort, { once: true });
    let rejectAbort: () => void = () => undefined;
    const aborted = new Promise<never>((_, reject) => {
      rejectAbort = () =>
        reject(new Error('Connection proof cancelled or timed out'));
    });
    operation.signal.addEventListener('abort', rejectAbort, { once: true });
    const timer = setTimeout(abort, 15000);
    const pending = {
      frames: new Map<string, string>(),
      wake: () => undefined as void,
    };
    this.pending = pending;
    const work = async () => {
      const id = await this.api.challenge(connectionId, operation.signal);
      if (operation.signal.aborted)
        throw new Error('Connection proof cancelled');
      while (!pending.frames.has(id)) {
        await Promise.race([
          new Promise<void>((resolve) => (pending.wake = resolve)),
          aborted,
        ]);
      }
      if (operation.signal.aborted)
        throw new Error('Connection proof cancelled');
      return this.api.confirm(id, pending.frames.get(id)!, operation.signal);
    };
    try {
      return await Promise.race([work(), aborted]);
    } finally {
      this.pending = undefined;
      pending.frames.clear();
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      operation.signal.removeEventListener('abort', rejectAbort);
    }
  }
}
